import { c as _c } from "react/compiler-runtime";
// «Образы» покупателя (fd-discovery-04): общие куски экранов кабинета и
// кнопки «В подборку» на карточке товара.
//
//   useLooks()        — список образов вошедшего покупателя (react-query);
//   LookNameDialog    — «Новый образ» / «Переименовать»: одно поле названия;
//   AddToLookDialog   — «В подборку» из карточки товара: отметить образы, в
//                       которых лежит товар, или создать новый сразу с ним.
//
// Окна — те же, что у «Способов оплаты» (r20, белые, заголовок 20/24 по
// центру, крестик справа), кнопки — DS «dark»/«gray».
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@osmix/ui/dialog";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { api, type Look } from "@/api";
import { CloseIcon, PlusIcon } from "@/components/icons";
import { Button } from "@/components/ui/button";
import { TextField } from "@/components/ui/text-field";
import { cn } from "@/lib/utils";
import { useProfile } from "@/store";
const OVERLAY = "bg-black/20 backdrop-blur-[2px]";
export const looksKey = (userId: string) => ["buyer", userId, "looks"] as const;
export function useLooks(t0) {
  const $ = _c(5);
  const enabled = t0 === undefined ? true : t0;
  const userId = useProfile(_temp);
  const signedIn = useProfile(_temp2);
  let t1;
  if ($[0] !== userId) {
    t1 = looksKey(userId);
    $[0] = userId;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const t2 = enabled && signedIn;
  let t3;
  if ($[2] !== t1 || $[3] !== t2) {
    t3 = {
      queryKey: t1,
      queryFn: _temp3,
      enabled: t2
    };
    $[2] = t1;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  return useQuery(t3);
}

/** Обновить список и сам образ в кэше после правки. */
function _temp3() {
  return api.looks.list();
}
function _temp2(s_0) {
  return Boolean(s_0.token);
}
function _temp(s) {
  return s.user?.id ?? "guest";
}
export function useLookCache() {
  const $ = _c(3);
  const userId = useProfile(_temp4);
  const qc = useQueryClient();
  let t0;
  if ($[0] !== qc || $[1] !== userId) {
    t0 = look => {
      if (look) {
        qc.setQueryData(["buyer", userId, "look", look.id], look);
      }
      qc.invalidateQueries({
        queryKey: looksKey(userId)
      });
    };
    $[0] = qc;
    $[1] = userId;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  return t0;
}
function _temp4(s) {
  return s.user?.id ?? "guest";
}
export const productCountLabel = (n: number): string => {
  const mod10 = n % 10;
  const mod100 = n % 100;
  const word = mod10 === 1 && mod100 !== 11 ? "товар" : mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14) ? "товара" : "товаров";
  return `${n} ${word}`;
};
function DialogShell(t0) {
  const $ = _c(23);
  const {
    open,
    onClose,
    title,
    description,
    children
  } = t0;
  let t1;
  if ($[0] !== onClose) {
    t1 = o => !o && onClose();
    $[0] = onClose;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      maxWidth: "min(440px, calc(100% - 2rem))"
    };
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== title) {
    t3 = <DialogTitle className="text-center text-[20px] leading-6 font-bold tracking-[-0.6px] text-[#161616]">{title}</DialogTitle>;
    $[3] = title;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <CloseIcon size={24} />;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== onClose) {
    t5 = <button type="button" aria-label={"\u0417\u0430\u043A\u0440\u044B\u0442\u044C"} onClick={onClose} className="absolute top-4 right-4 grid size-6 place-items-center rounded-full outline-none hover:bg-ds-quaternary focus-visible:ring-2 focus-visible:ring-ds-focus">{t4}</button>;
    $[6] = onClose;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== t3 || $[9] !== t5) {
    t6 = <div className="relative flex h-14 items-center justify-center px-14">{t3}{t5}</div>;
    $[8] = t3;
    $[9] = t5;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== description) {
    t7 = <DialogDescription className="sr-only">{description}</DialogDescription>;
    $[11] = description;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== children) {
    t8 = <div className="flex flex-col gap-3 px-4 pt-2.5 pb-4">{children}</div>;
    $[13] = children;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== t6 || $[16] !== t7 || $[17] !== t8) {
    t9 = <DialogContent overlayClassName={OVERLAY} showCloseButton={false} onOpenAutoFocus={_temp5} style={t2} className="gap-0 rounded-[20px] border-0 bg-white p-0 shadow-none sm:max-w-none">{t6}{t7}{t8}</DialogContent>;
    $[15] = t6;
    $[16] = t7;
    $[17] = t8;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] !== open || $[20] !== t1 || $[21] !== t9) {
    t10 = <Dialog open={open} onOpenChange={t1}>{t9}</Dialog>;
    $[19] = open;
    $[20] = t1;
    $[21] = t9;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  return t10;
}

/** Название образа: создать новый или переименовать. */
function _temp5(e) {
  return e.preventDefault();
}
export function LookNameDialog({
  open,
  onClose,
  initial = "",
  title,
  submitLabel,
  onSubmit
}: {
  open: boolean;
  onClose: () => void;
  initial?: string;
  title: string;
  submitLabel: string;
  onSubmit: (name: string) => Promise<void>;
}) {
  const [name, setName] = useState(initial);
  const [busy, setBusy] = useState(false);
  const trimmed = name.trim();
  const submit = async () => {
    if (!trimmed || busy) return;
    setBusy(true);
    try {
      await onSubmit(trimmed);
      onClose();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Не удалось сохранить");
    } finally {
      setBusy(false);
    }
  };
  return <DialogShell open={open} onClose={onClose} title={title} description="Название образа">
      <form className="flex flex-col gap-3" onSubmit={e_0 => {
      e_0.preventDefault();
      void submit();
    }}>
        <TextField autoFocus label="Название" placeholder="Например, «На осень»" value={name} maxLength={160} onChange={e_1 => setName(e_1.target.value)} data-testid="look-name-input" />
        <Button type="submit" block disabled={!trimmed || busy} data-testid="look-name-submit">
          {submitLabel}
        </Button>
      </form>
    </DialogShell>;
}

/**
 * «В подборку» из карточки товара: образы с отметкой «товар уже здесь»
 * (нажатие кладёт или убирает) и «Новый образ» с этим товаром.
 */
export function AddToLookDialog(t0) {
  const $ = _c(32);
  const {
    open,
    onClose,
    productId
  } = t0;
  const looks = useLooks(open);
  const refresh = useLookCache();
  const [creating, setCreating] = useState(false);
  let t1;
  if ($[0] !== productId) {
    t1 = async look => look.productIds.includes(productId) ? api.looks.removeProduct(look.id, productId) : api.looks.addProduct(look.id, productId);
    $[0] = productId;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== productId || $[3] !== refresh) {
    t2 = (look_0, prev) => {
      refresh(look_0);
      toast.success(prev.productIds.includes(productId) ? `Убрано из «${look_0.title}»` : `Добавлено в «${look_0.title}»`);
    };
    $[2] = productId;
    $[3] = refresh;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== t1 || $[6] !== t2) {
    t3 = {
      mutationFn: t1,
      onSuccess: t2,
      onError: _temp6
    };
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const toggle = useMutation(t3);
  let t4;
  if ($[8] !== looks.data) {
    t4 = looks.data ?? [];
    $[8] = looks.data;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const list = t4;
  const t5 = open && !creating;
  let t6;
  if ($[10] !== list || $[11] !== looks.isPending || $[12] !== productId || $[13] !== toggle) {
    t6 = looks.isPending ? <p className="py-4 text-center text-[14px] text-ds-secondary">Загружаем образы…</p> : list.length === 0 ? <p className="py-2 text-center text-[14px] leading-[18px] text-ds-secondary">Образов пока нет. Соберите первый - с этим товаром.</p> : <ul className="flex max-h-[320px] flex-col gap-1 overflow-y-auto" data-testid="look-picker">{list.map(look_1 => {
        const inLook = look_1.productIds.includes(productId);
        return <li key={look_1.id}><button type="button" disabled={toggle.isPending} onClick={() => toggle.mutate(look_1)} aria-pressed={inLook} className="flex w-full items-center gap-3 rounded-[12px] p-2 text-left outline-none transition-colors hover:bg-ds-gray focus-visible:ring-2 focus-visible:ring-ds-focus disabled:opacity-60"><span className="size-12 shrink-0 overflow-hidden rounded-[8px] bg-ds-gray">{look_1.cover ? <img src={look_1.cover} alt="" className="size-full object-cover" /> : null}</span><span className="flex min-w-0 flex-1 flex-col"><span className="truncate text-[16px] leading-5 font-semibold text-ds-primary">{look_1.title}</span><span className="text-[13px] leading-4 text-ds-secondary">{productCountLabel(look_1.productCount)}</span></span><span className={cn("grid size-6 shrink-0 place-items-center rounded-full border", inLook ? "border-ds-primary bg-ds-primary text-white" : "border-ds-line-2")}>{inLook ? <Check className="size-4" strokeWidth={2.5} /> : null}</span></button></li>;
      })}</ul>;
    $[10] = list;
    $[11] = looks.isPending;
    $[12] = productId;
    $[13] = toggle;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = () => setCreating(true);
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <Button variant="gray" block={true} onClick={t7} data-testid="look-create-from-product"><PlusIcon size={20} />Новый образ</Button>;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== onClose || $[18] !== t5 || $[19] !== t6) {
    t9 = <DialogShell open={t5} onClose={onClose} title={"\u0412 \u043F\u043E\u0434\u0431\u043E\u0440\u043A\u0443"} description={"\u0412\u044B\u0431\u0435\u0440\u0438\u0442\u0435 \u043E\u0431\u0440\u0430\u0437 \u0434\u043B\u044F \u0442\u043E\u0432\u0430\u0440\u0430"}>{t6}{t8}</DialogShell>;
    $[17] = onClose;
    $[18] = t5;
    $[19] = t6;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  const t10 = open && creating;
  let t11;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = () => setCreating(false);
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  let t12;
  if ($[22] !== onClose || $[23] !== productId || $[24] !== refresh) {
    t12 = async title => {
      const look_2 = await api.looks.create({
        title,
        productIds: [productId]
      });
      refresh(look_2);
      toast.success(`Добавлено в «${look_2.title}»`);
      onClose();
    };
    $[22] = onClose;
    $[23] = productId;
    $[24] = refresh;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  let t13;
  if ($[26] !== t10 || $[27] !== t12) {
    t13 = <LookNameDialog open={t10} onClose={t11} title={"\u041D\u043E\u0432\u044B\u0439 \u043E\u0431\u0440\u0430\u0437"} submitLabel={"\u0421\u043E\u0437\u0434\u0430\u0442\u044C \u0438 \u0434\u043E\u0431\u0430\u0432\u0438\u0442\u044C"} onSubmit={t12} />;
    $[26] = t10;
    $[27] = t12;
    $[28] = t13;
  } else {
    t13 = $[28];
  }
  let t14;
  if ($[29] !== t13 || $[30] !== t9) {
    t14 = <>{t9}{t13}</>;
    $[29] = t13;
    $[30] = t9;
    $[31] = t14;
  } else {
    t14 = $[31];
  }
  return t14;
}
function _temp6(e) {
  return toast.error(e instanceof Error ? e.message : "\u041D\u0435 \u0443\u0434\u0430\u043B\u043E\u0441\u044C \u0441\u043E\u0445\u0440\u0430\u043D\u0438\u0442\u044C");
}